import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, Plus } from "lucide-react";
import { TIMELINE } from "@/data/content";
import { Reveal, ChapterBadge } from "@/components/Reveal";

export default function Experience() {
  const [openId, setOpenId] = useState(TIMELINE[0].id);

  return (
    <section id="experience" data-testid="experience-section" className="py-20 sm:py-28">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <ChapterBadge number="03" label="Career Evolution" />
        <Reveal className="mt-8 max-w-2xl">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-charcoal-900 leading-[1.1]">
            14+ years, nine roles, <em className="italic text-copper-600">one trajectory.</em>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-charcoal-600 leading-relaxed">
            From graphics and web design to senior creative operations leadership — a career built
            across FMCG, consulting, advertising, publishing and media.
          </p>
        </Reveal>

        <div className="mt-14 relative">
          <span className="absolute left-[7px] top-2 bottom-2 w-px bg-charcoal-900/15" aria-hidden="true" />
          <div className="flex flex-col gap-3">
            {TIMELINE.map((item, i) => {
              const open = openId === item.id;
              return (
                <Reveal key={item.id} delay={Math.min(i * 0.05, 0.3)}>
                  <div className="relative pl-10">
                    <span
                      className={`absolute left-0 top-7 w-[15px] h-[15px] rounded-full border-2 transition-colors duration-300 ${
                        open ? "bg-copper-600 border-copper-600" : "bg-parchment border-charcoal-900/30"
                      }`}
                      aria-hidden="true"
                    />
                    <button
                      data-testid={`timeline-role-${item.id}`}
                      onClick={() => setOpenId(open ? null : item.id)}
                      aria-expanded={open}
                      className={`w-full text-left rounded-2xl border p-6 sm:p-7 transition-[border-color,box-shadow,background-color] duration-300 ${
                        open
                          ? "bg-surface-white border-copper-600/40 shadow-lg"
                          : "bg-surface-white/60 border-charcoal-900/10 hover:border-charcoal-900/25"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-charcoal-500">
                              {item.period}
                            </span>
                            {item.current && (
                              <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-copper-600 bg-copper-100 border border-copper-600/25 rounded-full px-2.5 py-0.5">
                                Current
                              </span>
                            )}
                          </div>
                          <h3 className="mt-2 text-lg sm:text-xl font-semibold text-charcoal-900">{item.role}</h3>
                          <p className="mt-1 text-sm sm:text-base text-charcoal-600">
                            {item.company}
                            {item.division ? ` | ${item.division}` : ""}
                          </p>
                          <p className="mt-1.5 flex items-center gap-1.5 font-mono text-xs text-charcoal-500">
                            <MapPin className="w-3.5 h-3.5" /> {item.location}
                          </p>
                        </div>
                        <motion.span
                          animate={{ rotate: open ? 45 : 0 }}
                          transition={{ duration: 0.25 }}
                          className={`mt-1 shrink-0 w-8 h-8 rounded-full border flex items-center justify-center ${
                            open ? "border-copper-600 text-copper-600" : "border-charcoal-900/20 text-charcoal-500"
                          }`}
                        >
                          <Plus className="w-4 h-4" />
                        </motion.span>
                      </div>

                      <AnimatePresence initial={false}>
                        {open && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                            className="overflow-hidden"
                          >
                            <ul className="pt-5 mt-5 border-t border-charcoal-900/10 flex flex-col gap-2.5">
                              {item.points.map((point, j) => (
                                <li key={j} className="flex gap-3 text-sm sm:text-[15px] leading-relaxed text-charcoal-600">
                                  <span className="mt-[9px] w-4 h-px bg-copper-600/60 shrink-0" aria-hidden="true" />
                                  {point}
                                </li>
                              ))}
                            </ul>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </button>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
